import { createContext, useEffect, useReducer, ReactElement } from "react"
import { Chance } from "chance"
import jwtDecode from "jwt-decode"
import { LOGIN, LOGOUT, RELOAD_USER, UPDATE_USER } from "store/reducers/actions"
import authReducer from "store/reducers/auth"
import Loader from "components/Loader"
import axios from "utils/axios"
import { KeyedObject } from "types/root"
import { AuthProps, JWTContextType, UserProfile } from "types/auth"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { decodeData, getArtistCode } from "utils"
import { bootIntercom, shutdownIntercom } from "utils/intercom"

const chance = new Chance()

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  roleIds: [],
  user: null
}

export const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem("serviceToken", serviceToken)
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`
  } else {
    localStorage.removeItem("serviceToken")
    delete axios.defaults.headers.common.Authorization
  }
}

const verifyToken = async (serviceToken: string) => {
  if (!serviceToken) {
    return false
  }
  const decoded: KeyedObject = jwtDecode(serviceToken)

  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */

  let isValid = decoded.exp > Date.now() / 1000

  if (isValid) {
    setSession(serviceToken)
  } else {
    axios
      .post("/refresh/", {}, { withCredentials: true })
      .then((tokenResponse) => {
        if (tokenResponse?.data?.token) {
          const token = tokenResponse.data.token
          setSession(token)
          isValid = true
        } else {
          setSession(null)
        }
      })
      .catch(() => {
        setSession(null)
      })
  }

  return isValid
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null)

export const JWTProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const [searchParams, setSearchParams] = useSearchParams()

  const navigate = useNavigate()

  useEffect(() => {
    const init = async () => {
      let advanceFromRoyalty = false
      if (searchParams.has("royalty")) {
        const param = searchParams.get("royalty")
        const decodedTokenData = decodeData(param)

        if (!decodedTokenData.expired) {
          setSession(decodedTokenData.token)
          advanceFromRoyalty = true
        }

        searchParams.delete("royalty")
        setSearchParams(searchParams)
      }

      try {
        const serviceToken = localStorage.getItem("serviceToken")

        if (serviceToken) {
          const isTokenValid = await verifyToken(serviceToken)
          if (!isTokenValid) {
            dispatch({
              type: LOGOUT
            })
            return
          }

          const response = await axios.get("/me/")
          const user = response.data as UserProfile

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              roleIds: user.role_ids,
              user
            }
          })

          // Boot Intercom with user data
          bootIntercom(user)

          const url = window.location.href

          const validArtistResponse = await axios.get(`/public/offers/validate_link/?artist_code=${getArtistCode()}`)
          const validArtist = validArtistResponse.data.artist
          if (validArtist) {
            if (!validArtist.is_admin) {
              localStorage.setItem("offersArtistId", validArtist.artist_id)
              localStorage.setItem("offersArtistName", validArtist.name)
              localStorage.setItem("offersCover", validArtist.image)
              localStorage.setItem("offersArtistEmail", validArtist.email)
            }

            localStorage.setItem("personalizedOffer", validArtist.is_admin ? "0" : "1")
          } else {
            dispatch({
              type: LOGOUT
            })
            return
          }

          if (
            !url.includes("offer=1") &&
            !url.includes("deal=1") &&
            !url.includes("thanks") &&
            !advanceFromRoyalty &&
            !url.includes("account")
          ) {
            navigate(`${getArtistCode()}/artist/overview`)
          }
        } else {
          dispatch({
            type: LOGOUT
          })
        }
      } catch (err) {
        console.error(err)
        dispatch({
          type: LOGOUT
        })
      }
    }

    init().then()
  }, [])

  const login = async (username: string, password: string) => {
    const response = await axios.post("/login/", { username, password }, { withCredentials: true })

    const { serviceToken, user } = { serviceToken: response.data.token, user: response.data as UserProfile }
    setSession(serviceToken)
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        roleIds: user.role_ids,
        user
      }
    })

    // Boot Intercom with user data
    bootIntercom(user)
  }

  const offersAuthenticate = async (username: string, password: string, ipAddress: string, codeEnabled: boolean) => {
    const response = await axios.post(
      "/authentication/",
      {
        username,
        password,
        ip_address: ipAddress,
        code_enabled: codeEnabled,
        platform: "offers"
      },
      { withCredentials: true }
    )
    if (!response.data.success) {
      return response.data
    }

    if (response.data.success && !response.data.user) {
      return response.data
    }

    const { serviceToken, user } = { serviceToken: response.data.user.token, user: response.data.user as UserProfile }
    setSession(serviceToken)
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        roleIds: user.role_ids,
        user
      }
    })

    // Boot Intercom with user data
    bootIntercom(user)

    return response.data
  }

  const forgotPasswordSendCode = async (username: string) => {
    const response = await axios.post("/forgot-password/", { username, platform: "offers", ip_address: "127.0.0.1" })
    return response.data.success
  }

  const offersCodeValidation = async (
    username: string,
    password: string,
    name: string,
    code: string,
    country: string,
    leadFollow?: string,
    resetPassword?: boolean,
    spotifyId?: string,
    artistName?: string
  ) => {
    const response = await axios.post(
      "/code-validation/",
      {
        username,
        password,
        name,
        code,
        country,
        platform: "offers",
        lead_follow: leadFollow,
        reset_password: resetPassword,
        spotify_id: spotifyId,
        artist_name: artistName
      },
      { withCredentials: true }
    )
    if (!response.data.success) {
      return false
    }

    const { serviceToken, user } = { serviceToken: response.data.user.token, user: response.data.user as UserProfile }
    setSession(serviceToken)
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        roleIds: user.role_ids,
        user
      }
    })

    // Boot Intercom with user data
    bootIntercom(user)

    return true
  }

  const loginSpotify = async (code: string) => {
    const response = await axios.post(`/spotify/?code=${code}`, null, {
      withCredentials: true
    })

    const { serviceToken, user } = { serviceToken: response.data.token, user: response.data as UserProfile }
    setSession(serviceToken)
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        roleIds: user.role_ids,
        user
      }
    })

    // Boot Intercom with user data
    bootIntercom(user)
  }

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin()
    const response = await axios.post("/api/account/register", {
      id,
      email,
      password,
      firstName,
      lastName
    })
    let users = response.data

    if (window.localStorage.getItem("users") !== undefined && window.localStorage.getItem("users") !== null) {
      const localUsers = window.localStorage.getItem("users")
      users = [
        ...JSON.parse(localUsers!),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ]
    }

    window.localStorage.setItem("users", JSON.stringify(users))
  }

  const logout = () => {
    // Shutdown Intercom on logout
    shutdownIntercom()

    setSession(null)
    dispatch({ type: LOGOUT })
  }

  const resetPassword = async (email: string) => {}

  const reloadProfile = () => {
    dispatch({
      type: RELOAD_USER,
      payload: {
        ...state
      }
    })
  }

  const updateUser = (user: UserProfile | null | undefined) => {
    dispatch({
      type: UPDATE_USER,
      payload: {
        ...state,
        user
      }
    })
  }

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        loginSpotify,
        offersAuthenticate,
        offersCodeValidation,
        logout,
        updateUser,
        register,
        resetPassword,
        reloadProfile,
        forgotPasswordSendCode
      }}
    >
      {children}
    </JWTContext.Provider>
  )
}

export default JWTContext
